import { useAuth } from '@/features/authentication';
import { LogoIcon } from '@/shared/brand';
import { Button } from '@/shared/ui/button';
import { Footer } from '@/widgets/footer';
import { Navbar } from '@/widgets/navbar';
import {
  ArrowRight,
  CheckCircle2,
  Heart,
  MessageCircle,
  Shield,
  Sparkles,
  Star,
  Users,
} from 'lucide-react';
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function HomePage() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, isLoading, navigate]);

  const features = [
    {
      icon: Users,
      title: 'Profils vérifiés',
      description:
        'Chaque profil est vérifié manuellement pour garantir authenticité et sérieux des intentions.',
    },
    {
      icon: Shield,
      title: 'Confidentialité respectée',
      description:
        'Votre vie privée est sacrée. Vous contrôlez qui peut voir votre profil et vos photos.',
    },
    {
      icon: MessageCircle,
      title: 'Échanges sécurisés',
      description: 'Communiquez en toute confiance via notre messagerie chiffrée et modérée.',
    },
    {
      icon: Heart,
      title: 'Compatibilité réelle',
      description:
        'Notre système analyse vos valeurs et critères pour des introductions pertinentes.',
    },
  ];

  const stats = [
    { value: '50K+', label: 'Membres vérifiés' },
    { value: '10K+', label: 'Mariages célébrés' },
    { value: '95%', label: 'Taux de satisfaction' },
  ];

  const testimonials = [
    {
      quote:
        "Grâce à Zawajme, j'ai rencontré mon époux. Le sérieux de la plateforme nous a permis de nous connaître en toute confiance.",
      author: 'Fatima H.',
      location: 'Paris',
    },
    {
      quote:
        "Après des mois de recherche, j'ai trouvé une personne qui partage mes valeurs. Nous nous marions bientôt.",
      author: 'Ahmed K.',
      location: 'Lyon',
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative overflow-hidden py-24 md:py-36 pattern-geometric">
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-primary-surface to-transparent rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 opacity-60" />
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-accent/10 to-transparent rounded-full blur-3xl translate-x-1/3 translate-y-1/3 opacity-60" />

          <div className="container mx-auto relative flex flex-col items-center text-center px-4">
            {/* Hero Logo */}
            <div className="relative mb-8 animate-fade-in">
              <div className="absolute inset-0 bg-primary/20 rounded-full blur-2xl scale-150" />
              <div className="relative">
                <LogoIcon size={80} className="shadow-[var(--shadow-primary-lg)]" />
              </div>
            </div>

            {/* Trust Badge */}
            <div
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-surface border border-primary/20 text-primary text-sm font-medium mb-8 animate-fade-in"
              style={{ animationDelay: '0.1s' }}
            >
              <Sparkles className="h-4 w-4" />
              <span>Plus de 10 000 mariages célébrés</span>
            </div>

            <h1
              className="font-display text-4xl font-semibold tracking-tight sm:text-5xl md:text-6xl lg:text-7xl max-w-4xl animate-slide-up"
              style={{ animationDelay: '0.2s' }}
            >
              Trouvez votre <span className="text-gradient-primary">partenaire de vie</span>
            </h1>

            <p
              className="mt-8 max-w-2xl text-lg md:text-xl text-muted-foreground leading-relaxed animate-fade-in"
              style={{ animationDelay: '0.2s' }}
            >
              Zawajme est la plateforme de confiance pour ceux qui recherchent une relation sérieuse
              menant au mariage. Rencontrez des personnes qui partagent vos valeurs.
            </p>

            <div
              className="mt-12 flex flex-col sm:flex-row gap-4 animate-fade-in"
              style={{ animationDelay: '0.3s' }}
            >
              <Button size="xl" asChild>
                <Link to="/register">
                  Commencer mon parcours
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Link>
              </Button>
              <Button size="xl" variant="outline" asChild>
                <Link to="/login">Se connecter</Link>
              </Button>
            </div>

            {/* Stats */}
            <div
              className="mt-20 grid grid-cols-3 gap-8 md:gap-16 w-full max-w-2xl animate-fade-in"
              style={{ animationDelay: '0.4s' }}
            >
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-3xl md:text-4xl font-display font-semibold text-primary">
                    {stat.value}
                  </div>
                  <div className="text-sm text-muted-foreground mt-2">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Indicators */}
        <section className="py-8 border-y border-border bg-card">
          <div className="container mx-auto">
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 text-muted-foreground">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium">Données chiffrées SSL</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-success" />
                <span className="text-sm font-medium">Profils vérifiés manuellement</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="h-5 w-5 text-accent" />
                <span className="text-sm font-medium">Service premium depuis 2015</span>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-24 md:py-32">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl md:text-5xl">
                Pourquoi choisir <span className="text-primary">Zawajme</span> ?
              </h2>
              <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                Une plateforme conçue pour les personnes sérieuses dans leur recherche d&apos;un
                partenaire de vie.
              </p>
            </div>
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4 stagger-children">
              {features.map((feature) => (
                <div
                  key={feature.title}
                  className="group flex flex-col items-center text-center p-8 rounded-xl border border-border bg-card transition-all duration-300 hover:shadow-xl hover:-translate-y-1 hover:border-primary/30"
                >
                  <div className="h-14 w-14 rounded-xl bg-primary-surface flex items-center justify-center mb-6 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                    <feature.icon className="h-7 w-7 text-primary group-hover:text-primary-foreground transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-24 md:py-32 bg-gradient-to-b from-primary-surface/50 to-background pattern-geometric">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                Votre parcours vers le mariage
              </h2>
              <p className="mt-6 text-lg text-muted-foreground">
                Un processus simple et respectueux en quatre étapes
              </p>
            </div>
            <div className="grid gap-8 md:grid-cols-4 max-w-5xl mx-auto">
              {[
                {
                  step: '1',
                  title: 'Créez votre profil',
                  description:
                    'Inscrivez-vous et complétez votre profil avec vos valeurs et critères importants.',
                },
                {
                  step: '2',
                  title: 'Vérification',
                  description:
                    'Notre équipe vérifie votre profil pour garantir le sérieux de la communauté.',
                },
                {
                  step: '3',
                  title: 'Découvrez',
                  description:
                    "Parcourez des profils compatibles et envoyez des demandes d'introduction.",
                },
                {
                  step: '4',
                  title: 'Connectez-vous',
                  description:
                    'Échangez en toute confiance et construisez une relation authentique.',
                },
              ].map((item, index) => (
                <div key={item.step} className="relative text-center">
                  <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-2xl font-display font-semibold mb-6 shadow-[var(--shadow-primary)]">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                  {index < 3 && (
                    <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-[2px] bg-gradient-to-r from-primary/30 to-primary/10" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-24 md:py-32">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                Ils ont trouvé leur moitié
              </h2>
              <p className="mt-6 text-lg text-muted-foreground">
                Découvrez les témoignages de nos membres
              </p>
            </div>
            <div className="grid gap-8 md:grid-cols-2 max-w-4xl mx-auto">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="p-8 rounded-xl border border-border bg-card shadow-sm">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-accent text-accent" />
                    ))}
                  </div>
                  <blockquote className="text-lg text-foreground leading-relaxed mb-6">
                    &ldquo;{testimonial.quote}&rdquo;
                  </blockquote>
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-primary-surface flex items-center justify-center">
                      <span className="text-primary font-semibold">{testimonial.author[0]}</span>
                    </div>
                    <div>
                      <div className="font-semibold">{testimonial.author}</div>
                      <div className="text-sm text-muted-foreground">{testimonial.location}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-24 md:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary to-primary-dark" />
          <div className="absolute inset-0 pattern-geometric opacity-10" />

          <div className="container mx-auto relative text-center text-primary-foreground px-4">
            <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl md:text-5xl">
              Prêt(e) à commencer votre histoire ?
            </h2>
            <p className="mt-6 text-lg md:text-xl opacity-90 max-w-2xl mx-auto leading-relaxed">
              Rejoignez Zawajme et faites le premier pas vers une relation sincère et durable.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="xl" variant="premium" asChild>
                <Link to="/register">
                  <Heart className="h-5 w-5 mr-2" />
                  Créer mon profil gratuitement
                </Link>
              </Button>
            </div>
            <div className="mt-10 flex flex-wrap justify-center gap-8 text-sm opacity-90">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" />
                <span>Inscription gratuite</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" />
                <span>Profils vérifiés</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" />
                <span>Données sécurisées</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" />
                <span>Support réactif</span>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
